import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import api, { fileUrl, formatPrice, localized, currencyForLang, apiError } from "@/lib/api";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const productHref = (product, lang) => {
  const isHr = (lang || "en").startsWith("hr");
  if (isHr && product.slug_hr) return `/proizvod/${product.slug_hr}`;
  if (product.slug) return `/p/${product.slug}`;
  return `/product/${product.product_id}`;
};

export default function Cart() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { items, updateItem, removeItem, subtotal } = useCart();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    if (!user) return navigate("/login?next=/cart");
    setBusy(true);
    try {
      const { data } = await api.post("/checkout/session", {
        origin_url: window.location.origin,
        currency: currencyForLang(lang).toLowerCase(),
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e, t, "errors.generic"));
      setBusy(false);
    }
  };

  const shipping = items.length ? 7.5 : 0;
  const total = subtotal + shipping;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="cart-page">
      <p className="overline">{t("cart.overline")}</p>
      <h1 className="font-serif text-5xl tracking-tight mt-2">{t("cart.title")}</h1>

      {items.length === 0 ? (
        <div className="mt-16 bg-boneAlt rounded-sm py-20 text-center" data-testid="cart-empty">
          <p className="font-serif text-3xl">{t("cart.empty")}</p>
          <Link to="/shop" className="inline-block mt-6 bg-olive text-bone px-8 py-4 rounded-sm hover:bg-olive-dark transition-colors text-sm">{t("cart.discover")}</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12 mt-12">
          <div className="space-y-6" data-testid="cart-items">
            {items.map(({ product, quantity }) => (
              <div key={product.product_id} className="flex gap-6 bg-white hairline rounded-sm p-6" data-testid={`cart-item-${product.product_id}`}>
                <Link to={productHref(product, lang)} className="w-28 h-28 bg-boneAlt rounded-sm overflow-hidden flex-shrink-0">
                  {product.images?.[0] && <img src={fileUrl(product.images[0])} alt={product.title} className="w-full h-full object-cover" />}
                </Link>
                <div className="flex-1 min-w-0">
                  <p className="overline">{localized(product, "region", lang)}</p>
                  <Link to={productHref(product, lang)} className="font-serif text-xl block mt-1 hover:text-olive">{localized(product, "title", lang)}</Link>
                  <p className="text-sm text-ink-soft">{product.vendor_name} · {product.volume_ml}ml</p>
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center hairline rounded-sm bg-bone">
                      <button onClick={() => updateItem(product.product_id, quantity - 1)} className="px-3 py-2" data-testid={`cart-minus-${product.product_id}`}>−</button>
                      <span className="px-3 text-sm">{quantity}</span>
                      <button onClick={() => updateItem(product.product_id, quantity + 1)} className="px-3 py-2" data-testid={`cart-plus-${product.product_id}`}>+</button>
                    </div>
                    <span className="font-medium" data-testid={`cart-subtotal-${product.product_id}`}>{formatPrice(product.price * quantity, lang)}</span>
                  </div>
                </div>
                <button onClick={() => removeItem(product.product_id)} className="text-ink-muted hover:text-terracotta self-start" data-testid={`cart-remove-${product.product_id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <aside className="bg-boneAlt rounded-sm p-8 h-fit sticky top-28" data-testid="cart-summary">
            <h2 className="font-serif text-2xl">{t("cart.summary")}</h2>
            <div className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-ink-soft">{t("cart.subtotal")}</span><span data-testid="cart-subtotal">{formatPrice(subtotal, lang)}</span></div>
              <div className="flex justify-between"><span className="text-ink-soft">{t("cart.shipping")}</span><span>{formatPrice(shipping, lang)}</span></div>
              <div className="border-t border-edge pt-3 flex justify-between font-medium text-base">
                <span>{t("cart.total")}</span><span data-testid="cart-total">{formatPrice(total, lang)}</span>
              </div>
            </div>
            <button onClick={checkout} disabled={busy} className="w-full mt-6 bg-olive text-bone py-4 rounded-sm hover:bg-olive-dark transition-colors text-sm disabled:opacity-60" data-testid="checkout-button">
              {busy ? t("cart.redirecting") : t("cart.checkoutStripe")}
            </button>
            <p className="text-xs text-ink-muted mt-3 text-center">{t("cart.secureNote")}</p>
          </aside>
        </div>
      )}
    </div>
  );
}
